'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import DivergenceTimeline from './DivergenceTimeline';
import { elbowConnector, pointsToPath, polylineLength } from './divergenceGeometry';
import { CTA_ASSESSMENT, CTA_BOOKING } from '@/components/ui/CtaLabel';

// A small, purely decorative technical connector near the heading — an
// elbow with one corner, echoing the ring's own connector lines. Fixed to a
// small local box rather than derived from page geometry, since it only
// needs to sit near the heading, not point at anything real.
const TAG_CONNECTOR_POINTS = elbowConnector({ x: 0, y: 0 }, { x: 58, y: 34 }, 30)!;
const TAG_CONNECTOR_PATH = pointsToPath(TAG_CONNECTOR_POINTS);
const TAG_CONNECTOR_LENGTH = polylineLength(TAG_CONNECTOR_POINTS);

export default function HeroSection() {
  const [mounted, setMounted] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    setMounted(true);
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
  }, []);

  const motion = !prefersReducedMotion;

  return (
    <section
      data-ground
      id="hero"
      className="relative w-full hero-section m-ground"
      style={{ overflowX: 'hidden', overflowY: 'visible' }}
      aria-label="Noventi Labs — Divergence Timeline Hero"
    >
      <style>{`
        @keyframes hero-draw { to { stroke-dashoffset: 0; } }
        @keyframes hero-rise {
          from { opacity: 0; transform: translateY(14px); filter: blur(6px); }
          to   { opacity: 1; transform: translateY(0);    filter: blur(0); }
        }
        .hero-rise { animation: hero-rise .8s cubic-bezier(.22,1,.36,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .hero-rise { animation: none !important; }
        }

        /* The hero is a full-viewport column: the copy takes what it needs and
           the ring gets the rest. Sizing the ring off the leftover height —
           rather than off viewport width alone — is what keeps it on screen on
           short laptop displays, where width is generous but height is not. */
        .hero-section {
          min-height: 100vh;
          min-height: 100svh;
          display: flex;
          flex-direction: column;
        }
        .hero-ring-area {
          flex: 1 1 auto;
          /* min-height:0 lets this actually shrink inside the flex column;
             without it the ring would push the column past the viewport. */
          min-height: 0;
          display: flex;
          /* stretch, not center: a stretched flex item has a definite cross
             size, which is what lets the ring's height:100% child resolve.
             Against a centred (auto-height) item it resolves to zero and the
             ring disappears. The circle centres itself inside the box anyway. */
          align-items: stretch;
          justify-content: center;
          box-sizing: border-box;
          padding: 0 16px clamp(16px, 3vh, 48px);
        }
        .hero-ring {
          position: relative;
          width: min(74vw, 780px);
          max-width: 100%;
          /* Floor so a very short window shrinks the ring to a readable size
             and then scrolls, instead of collapsing it to nothing. */
          min-height: clamp(300px, 42vh, 600px);
        }

        /* Type scale lives here rather than inline so the laptop media query
           below can override it — an inline font-size would always win.

           This must outrank the section headings below it, which cap at 52px
           (see sectionHeading.ts). It used to cap at 46px, so on every screen
           the page's headline was set smaller than the six headings beneath
           it and read as a subtitle to its own page. */
        .hero-heading { font-size: clamp(34px, min(5.4vw, 5.6vh), 68px); }
        .hero-sub { font-size: clamp(13px, min(1.45vw, 2vh), 18px); }
        /* ── Laptop band (1024–1599px) ────────────────────────────────────
           Laptops are wide but short, so the ring — which is sized from the
           column's leftover height — ends up far smaller than on a desktop,
           where it is capped by width instead. Reclaim height from the
           heading and give the ring a bigger floor, so it reads as the
           dominant element the way it does at 1920+. Bounded above at 1599px
           so the desktop/ultrawide layout is untouched. */
        @media (min-width: 1024px) and (max-width: 1599px) {
          /* Still reclaims height from the heading relative to the desktop
             scale, but no longer below the 52px section headings — the ring
             gives up the difference instead. */
          .hero-heading { font-size: clamp(36px, 4.6vw, 62px); }
          .hero-sub { font-size: clamp(13px, 1.15vw, 16px); }
          .hero-ring { min-height: clamp(400px, 58vh, 620px); }
        }

        /* Compact screens keep the original width-driven ring and let the page
           scroll — there is no spare height to fit it into. */
        @media (max-width: 767px) {
          .hero-section { min-height: 0; display: block; }
          .hero-ring-area { padding-bottom: 32px; }
          .hero-ring {
            width: min(94vw, 440px);
            height: auto;
            aspect-ratio: 1 / 1;
            min-height: 0;
          }
        }
      `}</style>

      {/* Top system label */}
      <div
        className="w-full flex justify-center"
        style={{
          // Height-aware so the masthead gap collapses on short displays
          // instead of pushing the ring off screen. The floor clears the fixed
          // header, which overlays this section rather than sitting above it —
          // below that the label slides under the nav. Read from the header's
          // own height rather than restating it: the floor was 76px against a
          // 72px header, and the same 4px of daylight should survive the
          // header being shorter on a phone rather than turning into 20px.
          paddingTop: 'clamp(calc(var(--m-header-h) + 4px), 7vh, 96px)',
          paddingBottom: 8,
          paddingLeft: 20,
          paddingRight: 20,
          opacity: mounted ? 1 : 0,
          transition: prefersReducedMotion ? 'none' : 'opacity 0.8s ease',
        }}
      >
        <p className="m-eyebrow text-center" style={{ letterSpacing: '0.22em', maxWidth: 640 }}>
          {/* This slot used to read "Noventi Labs — Diagnostic System v1.0".
              The instrument metaphor is good, but nothing above the fold said
              what this company does, who it serves, or where it operates —
              those three facts lived only in the <title> tag and the footer.
              The eyebrow is the cheapest place on the page to put them, and it
              costs the headline nothing. The v1.0 string now labels the ring
              itself, where it describes something real. */}
          Marketing &amp; business consulting · SMBs · Europe &amp; Australia
        </p>
      </div>

      {/* ── HERO CONTENT BLOCK — centered, standalone, outside the circle ── */}
      <div
        className="w-full flex flex-col items-center text-center px-6"
        style={{
          paddingTop: 'clamp(10px, 2.4vh, 28px)',
          paddingBottom: 'clamp(16px, 3.6vh, 40px)',
          flex: '0 0 auto',
        }}
      >
        {/* Small decorative connector, angled corner, near the heading */}
        <svg
          width="70"
          height="40"
          viewBox="-4 -4 70 40"
          aria-hidden="true"
          className={mounted && motion ? 'hero-rise' : undefined}
          style={{
            opacity: mounted ? 1 : 0,
            marginBottom: 2,
            height: 'clamp(16px, 3vh, 40px)',
            width: 'auto',
            transition: motion ? undefined : 'opacity .6s ease',
          }}
        >
          <path
            d={TAG_CONNECTOR_PATH}
            fill="none"
            stroke="rgba(var(--m-ring-rgb),0.45)"
            strokeWidth={1}
            style={
              motion
                ? {
                    strokeDasharray: TAG_CONNECTOR_LENGTH,
                    strokeDashoffset: mounted ? 0 : TAG_CONNECTOR_LENGTH,
                    transition: 'stroke-dashoffset 1s cubic-bezier(.4,0,.2,1) .2s',
                  }
                : undefined
            }
          />
          <circle
            cx={TAG_CONNECTOR_POINTS[2].x}
            cy={TAG_CONNECTOR_POINTS[2].y}
            r={2}
            fill="rgba(var(--m-ring-rgb),.5)"
          />
        </svg>

        {/* eslint-disable-next-line jsx-a11y/heading-has-content */}
        <div className="m-reveal-line" style={{ maxWidth: 820, width: '100%' }}>
          <h1
            ref={headingRef}
            className={`uppercase font-bold hero-heading m-balance ${mounted && motion ? 'hero-rise' : ''}`}
            style={{
              fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
              color: 'var(--m-ink)',
              letterSpacing: '0.02em',
              /* 1.12 was loose for condensed caps at this size; 1.04 closes the
               lines into a single block, which is what makes a big headline
               read as one object rather than as three stacked rows. */
              lineHeight: 1.04,
              maxWidth: 820,
              textWrap: 'balance',
              opacity: mounted ? 1 : 0,
              transition: motion ? undefined : 'opacity .6s ease',
              animationDelay: motion ? '.1s' : undefined,
            }}
          >
            Identify the signals limiting your growth
          </h1>
        </div>

        <p
          className={`hero-sub ${mounted && motion ? 'hero-rise' : ''}`}
          style={{
            fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
            color: 'var(--m-ink-6)',
            lineHeight: 1.6,
            maxWidth: 560,
            marginTop: 'clamp(8px, 1.8vh, 16px)',
            opacity: mounted ? 1 : 0,
            transition: motion ? undefined : 'opacity .6s ease',
            animationDelay: motion ? '.22s' : undefined,
          }}
        >
          {/* The instrument metaphor is the brand's personality and the headline
              keeps it. But "signals" and "growth systems" are not words a first
              time visitor can decode, so the promise is restated in plain
              English underneath — at every width. This used to be the phone-only
              line, with desktop keeping the abstract original; two hero texts
              meant the better one was seen by half the visitors and the other
              was the one that got edited. */}
          We find out what is really holding your business back — then give you a clear plan to fix
          it.
        </p>

        <div
          /* w-full is what lets the two CTAs go full-width on a phone. It must
             not survive past that breakpoint: a full-width row inside the
             centred hero column left-aligns the buttons against the far edge on
             wide screens, because the row, not the buttons, is what the parent
             was centring. */
          className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 w-full sm:w-auto ${mounted && motion ? 'hero-rise' : ''}`}
          style={{
            marginTop: 'clamp(14px, 3vh, 28px)',
            opacity: mounted ? 1 : 0,
            transition: motion ? undefined : 'opacity .6s ease',
            animationDelay: motion ? '.34s' : undefined,
          }}
        >
          <Link href="/business-assessment" className="m-btn m-btn--primary">
            {/* One name per destination at every width. "Run your business diagnostic"
                described the mechanism; this describes what the visitor gets. */}
            {CTA_ASSESSMENT}
          </Link>
          <Link href="/book-a-consultation" className="m-btn m-btn--secondary">
            {CTA_BOOKING}
          </Link>
        </div>

        {/* What happens after the tap. The commonest reason a visitor does not
            press a booking button is not knowing what they are agreeing to. */}
        <p
          className="font-mono uppercase text-center"
          style={{
            fontSize: 11,
            letterSpacing: '0.1em',
            color: 'var(--m-ink-6)',
            marginTop: 16,
          }}
        >
          Free · 30 minutes · no sales pitch
        </p>
      </div>

      {/* ── INTERACTIVE SERVICE CIRCLE — takes the column's leftover height ── */}
      {/* ── START HERE — three doors, phones only ──────────────────────────
            A visitor who does not yet know what they need has, on desktop, a
            whole screen to survey. On a phone they have one. These three routes
            let them self-select in a single tap instead of scrolling the page
            to work out which part is meant for them. */}

      <div className="w-full hero-ring-area">
        <div className="hero-ring">{mounted && <DivergenceTimeline interactive animated />}</div>
      </div>

      <nav
        aria-label="Where to start"
        className="sm:hidden w-full"
        style={{ marginTop: 8, marginBottom: 8, paddingInline: 20 }}
      >
        <p
          className="font-mono uppercase text-center"
          style={{ fontSize: 11, letterSpacing: '0.18em', color: 'var(--m-ink-6)' }}
        >
          Where would you like to start?
        </p>
        {/* Each door is written in the visitor's own words, and each one says
              what it costs them — jump, five minutes, a call. Ordered by how
              much certainty the visitor already has, so the first person to
              recognise themselves stops reading there. The first label echoes
              the challenges section it links to, so the destination confirms
              the choice rather than surprising it. */}
        <ul style={{ marginTop: 10, display: 'grid', gap: 8 }}>
          {[
            {
              label: 'I know what is slowing us down',
              hint: 'Jump straight to the fix',
              href: '#challenges',
            },
            {
              label: "Something is off, I can't pin it down",
              hint: 'Free 5-minute diagnostic',
              href: '/business-assessment',
            },
            {
              label: "I'd rather just talk it through",
              hint: '30-minute call, no sales pitch',
              href: '/book-a-consultation',
            },
          ].map((door) => (
            <li key={door.label}>
              <Link
                href={door.href}
                className="flex items-center justify-between gap-3 w-full text-left"
                style={{
                  minHeight: 56,
                  padding: '10px 16px',
                  border: '1px solid rgba(var(--m-ink-rgb),0.14)',
                  background: 'var(--m-surface-2)',
                  textDecoration: 'none',
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: 'var(--m-ink)',
                      lineHeight: 1.25,
                    }}
                  >
                    {door.label}
                  </span>
                  <span
                    className="font-mono uppercase"
                    style={{
                      fontSize: 11,
                      letterSpacing: '0.06em',
                      color: 'var(--m-muted)',
                      marginTop: 3,
                    }}
                  >
                    {door.hint}
                  </span>
                </span>
                <span aria-hidden style={{ color: 'var(--m-muted-3)', fontSize: 18 }}>
                  →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Scroll hint */}
      <div
        className="w-full flex justify-center"
        style={{
          flex: '0 0 auto',
          paddingBottom: 'clamp(10px, 2.4vh, 24px)',
          opacity: mounted ? 1 : 0,
          transition: prefersReducedMotion ? 'none' : 'opacity 0.8s ease 1.4s',
        }}
      >
        <p
          className="font-mono text-[8px] uppercase tracking-widest"
          style={{ color: 'var(--m-muted-3)', letterSpacing: '0.16em' }}
        >
          Scroll to explore
        </p>
      </div>
    </section>
  );
}
